import React, { useRef, useEffect, useState } from "react";
import { Toggle } from "../../../Components/Toggle/Toggle";
import { formatPriceWithSignificantDecimals } from "../../utils/formatter";

// Helper function to format numbers with commas while preserving precision for small prices.
const formatWithCommas = (value) => {
  if (value === null || value === undefined || value === "") return "";

  // Handle non-string and non-number values safely
  if (typeof value !== "string" && typeof value !== "number") return "";

  const num = parseFloat(value);
  if (isNaN(num)) return "";

  // For prices, preserve enough decimals to show meaningful digits.
  // We deliberately avoid forcing 2 decimals because tokens like NIBI trade at ~0.00x.
  return formatPriceWithSignificantDecimals(num, 8, false);
};

const TPSLSection = ({
  isManualTPSL,
  setIsManualTPSL,
  longShort,
  tp,
  sl,
  tpPercentInput,
  slPercentInput,
  handleTpChange,
  handleSlChange,
  handleSlBlur,
  handleTpPercentInputChange,
  handleSlPercentInputChange,
  percentTP,
  percentSL,
  TP_EST,
  SL_EST,
  openPrice,
}) => {
  // Refs to keep track of cursor positions
  const tpPriceInputRef = useRef(null);
  const tpPercentInputRef = useRef(null);
  const slPriceInputRef = useRef(null);
  const slPercentInputRef = useRef(null);

  // Refs to store cursor positions
  const tpPriceSelectionStart = useRef(null);
  const tpPercentSelectionStart = useRef(null);
  const slPriceSelectionStart = useRef(null);
  const slPercentSelectionStart = useRef(null);

  // Format EST values with commas and fixed decimal
  const formattedTPEst = TP_EST !== undefined ? formatWithCommas(TP_EST) : "";
  const formattedSLEst = SL_EST !== undefined ? formatWithCommas(SL_EST) : "";

  // NEW: local focus states for each input
  const [isTpFocused, setIsTpFocused] = useState(false);
  const [isSlFocused, setIsSlFocused] = useState(false);
  const [isTpPercentFocused, setIsTpPercentFocused] = useState(false);
  const [isSlPercentFocused, setIsSlPercentFocused] = useState(false);

  // Effect hooks for cursor positions
  useEffect(() => {
    if (tpPriceInputRef.current && tpPriceSelectionStart.current !== null) {
      tpPriceInputRef.current.setSelectionRange(
        tpPriceSelectionStart.current,
        tpPriceSelectionStart.current
      );
    }
  }, [tp]);

  useEffect(() => {
    if (tpPercentInputRef.current && tpPercentSelectionStart.current !== null) {
      tpPercentInputRef.current.setSelectionRange(
        tpPercentSelectionStart.current,
        tpPercentSelectionStart.current
      );
    }
  }, [tpPercentInput]);

  useEffect(() => {
    if (slPriceInputRef.current && slPriceSelectionStart.current !== null) {
      slPriceInputRef.current.setSelectionRange(
        slPriceSelectionStart.current,
        slPriceSelectionStart.current
      );
    }
  }, [sl]);

  useEffect(() => {
    if (slPercentInputRef.current && slPercentSelectionStart.current !== null) {
      slPercentInputRef.current.setSelectionRange(
        slPercentSelectionStart.current,
        slPercentSelectionStart.current
      );
    }
  }, [slPercentInput]);

  // Handle Take Profit price change
  const handleTpPriceChange = (e) => {
    tpPriceSelectionStart.current = e.target.selectionStart;
    const val = e.target.value.replace(/,/g, "");

    if (val === "") {
      handleTpChange(null);
      return;
    }

    if (/^\d*\.?\d*$/.test(val)) {
      handleTpChange(val);
    }
  };

  // Handle Take Profit percent change
  const handleTpPercentChange = (e) => {
    tpPercentSelectionStart.current = e.target.selectionStart;
    const val = e.target.value.replace(/,/g, "");
    handleTpPercentInputChange(val);
  };

  // Handle Stop Loss price change
  const handleSlPriceChange = (e) => {
    slPriceSelectionStart.current = e.target.selectionStart;
    const val = e.target.value.replace(/,/g, "");

    if (val === "") {
      handleSlChange(null);
      return;
    }

    if (/^\d*\.?\d*$/.test(val)) {
      handleSlChange(val);
    }
  };

  // Handle Stop Loss percent change
  const handleSlPercentChange = (e) => {
    slPercentSelectionStart.current = e.target.selectionStart;
    const val = e.target.value.replace(/,/g, "");
    handleSlPercentInputChange(val);
  };

  // Common input box styles based on specifications
  const inputBoxStyle = "w-full h-[41px] bg-[#F8F4FCCC] rounded-[10px] border border-[#0000000D] px-4 py-[10px] outline-none";

  // Heading styles based on specifications
  const headingStyle = "text-base leading-[150%] mb-4";

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-gray-500">Manual TP/SL</div>
        <Toggle
          status={isManualTPSL}
          onChange={() => setIsManualTPSL(!isManualTPSL)}
        />
      </div>

      {isManualTPSL && (
        <div className="space-y-6">
          {/* Take Profit Section */}
          <div>
            <h2 className={headingStyle}>Take Profit</h2>
            <div className="flex gap-3">
              {/* Price Input */}
              <div className="relative flex-1 min-w-0">
                <input
                  ref={tpPriceInputRef}
                  type="text"
                  value={isTpFocused ? tp ?? "" : formatWithCommas(tp ?? "")}
                  onFocus={() => setIsTpFocused(true)}
                  onBlur={() => {
                    setIsTpFocused(false);
                    const rawVal = String(tp ?? "").replace(/,/g, "");
                    const parsed = parseFloat(rawVal);
                    if (!isNaN(parsed)) {
                      handleTpChange(parsed.toString());
                    }
                  }}
                  onChange={handleTpPriceChange}
                  placeholder="Price"
                  className={inputBoxStyle}
                />
              </div>

              {/* Profit Input with +$ prefix */}
              <div className="relative flex-1 min-w-0">
                <input
                  ref={tpPercentInputRef}
                  type="text"
                  value={isTpPercentFocused ? (tpPercentInput || "0.00") : (tpPercentInput ? formatWithCommas(tpPercentInput) : "")}
                  onFocus={() => {
                    setIsTpPercentFocused(true);
                    if (!tpPercentInput) {
                      handleTpPercentInputChange("0.00");
                    }
                  }}
                  onBlur={() => {
                    setIsTpPercentFocused(false);
                    const val = parseFloat(tpPercentInput);
                    if (!isNaN(val)) {
                      handleTpPercentInputChange(val.toFixed(2));
                    }
                  }}
                  onChange={handleTpPercentChange}
                  placeholder="Profit"
                  className={`${inputBoxStyle} pl-9`}
                  style={{ textIndent: "15px" }}
                />
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#38A569] text-xl">
                  +$
                </span>
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[#38A569]">
                  %
                </span>
              </div>
            </div>
          </div>
          
          {/* Close bet / Loss Section */}
          <div>
            <h2 className={headingStyle}>Stop Loss</h2>
            <div className="flex gap-3">
              {/* Price Input */}
              <div className="relative flex-1 min-w-0">
                <input
                  ref={slPriceInputRef}
                  type="text"
                  value={isSlFocused ? sl ?? "" : formatWithCommas(sl ?? "")}
                  onFocus={() => setIsSlFocused(true)}
                  onBlur={() => {
                    setIsSlFocused(false);
                    const rawVal = String(sl ?? "").replace(/,/g, "");
                    const parsed = parseFloat(rawVal);
                    if (!isNaN(parsed)) {
                      handleSlChange(parsed.toString());
                    }
                    if (handleSlBlur) {
                      handleSlBlur();
                    }
                  }}
                  onChange={handleSlPriceChange}
                  placeholder="Price"
                  className={inputBoxStyle}
                />
              </div>

              {/* Loss Input with -$ prefix */}
              <div className="relative flex-1 min-w-0">
                <input
                  ref={slPercentInputRef}
                  type="text"
                  value={isSlPercentFocused ? (slPercentInput || "0.00") : (slPercentInput ? formatWithCommas(slPercentInput) : "")}
                  onFocus={() => {
                    setIsSlPercentFocused(true);
                    if (!slPercentInput) {
                      handleSlPercentInputChange("0.00");
                    }
                  }}
                  onBlur={() => {
                    setIsSlPercentFocused(false);
                    const val = parseFloat(slPercentInput);
                    if (!isNaN(val)) {
                      handleSlPercentInputChange(val.toFixed(2));
                    }
                  }}
                  onChange={handleSlPercentChange}
                  placeholder="Loss"
                  className={`${inputBoxStyle} pl-9`}
                  style={{ textIndent: "15px" }}
                />
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#DB1F22] text-xl">
                  -$
                </span>
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[#DB1F22]">
                  %
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TPSLSection;
